import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth.service';
import { PhotoRecorderComponent } from 'src/app/shared/components/recorders/photo-recorder/photo-recorder.component';
import { ScreenRecorderComponent } from 'src/app/shared/components/recorders/screen-recorder/screen-recorder.component';
import { VideoRecorderComponent } from 'src/app/shared/components/recorders/video-recorder/video-recorder.component';
import { DynamicOverlayService, OVERLAY_DATA } from 'src/app/shared/services/dynamic-overlay/dynamic-overlay.service';
import { ContentStateService } from 'src/app/views/content/content-state.service';
import { GenerateContentComponent } from '../../../generate-content/generate-content.component';
import { ChunkFileUploaderComponent } from '../chunk-file-uploader/chunk-file-uploader.component';
import { CreateChunkService } from '../create-chunk.service';
// import { AddEditionOptionComponent } from '../options/add-edition-option.component';
import { firstValueFrom } from 'rxjs';
import { StorageService } from 'src/app/storage.service';
import { ChunkService } from '../../chunk.service';
import { AddCanvasOptionComponent } from '../options/add-canvas-option.component';
import { AddWhiteboardOptionComponent } from '../options/add-whiteboard-option.component';
import { AddCodeOptionComponent } from '../options/add-code-option.component';
import { AddMathEquationOptionComponent } from '../options/add-math-equation-option.component';
import { AddTableOptionComponent } from '../options/add-table-option.component';
import { AddTagOptionComponent } from '../options/add-tag-option.component';
import { AnalyzeMediaOptionComponent } from '../options/analyze-media-option.component';
import { DeleteChunkComponent } from '../options/delete-chunk.component';
import { FixCreationOptionComponent } from '../options/fix-creation-option.component';
import { GenerateEmbeddingsOptionComponent } from '../options/generate-embeddings-option.component';
import { GenerateVideoProcedureOptionComponent } from '../options/generate-video-procedure-option.component';
import { SelectChunkComponent } from '../options/select-chunk.component';
import { TranscribeVideoOptionComponent } from '../options/transcribe-video-option.component';

export interface CreateChunkOptionsOverlayData {
  mode: 'create-chunk' | 'modify-chunk' | 'edit-chunk';
  canUndo: boolean;
  canRedo: boolean;
  canCreate: boolean;
  creatingChunk: boolean;
  savingChunk: boolean;
  saveSuccess: boolean;
  contentID: string;
  courseID?: string;
  blogID?: string;
  chunk: any;
  chunkAboveId?: string;
  chunkBelowId?: string;
  context?: 'create' | 'controller';
  triggerRef: ElementRef;
}
@Component({
  selector: 'app-create-chunk-options-overlay',
  standalone: true,
  imports: [
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    GenerateContentComponent,
    ChunkFileUploaderComponent,
    VideoRecorderComponent,
    ScreenRecorderComponent,
    PhotoRecorderComponent,
    DeleteChunkComponent,
    MatDividerModule,
    AddTagOptionComponent,
    // AddEditionOptionComponent,
    FixCreationOptionComponent,
    GenerateEmbeddingsOptionComponent,
    GenerateVideoProcedureOptionComponent,
    AnalyzeMediaOptionComponent,
    TranscribeVideoOptionComponent,
    AddMathEquationOptionComponent,
    AddCodeOptionComponent,
    AddCanvasOptionComponent,
    AddWhiteboardOptionComponent,
    SelectChunkComponent,
    AddTableOptionComponent,
  ],
  templateUrl: './create-chunk-options-overlay.component.html',
  styleUrls: ['./create-chunk-options-overlay.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateChunkOptionsOverlayComponent {
  @Input() mode: 'create-chunk' | 'modify-chunk' | 'edit-chunk';
  @Input() canUndo: boolean;
  @Input() canRedo: boolean;
  @Input() canCreate: boolean;
  @Input() creatingChunk: boolean;
  @Input() savingChunk: boolean;
  @Input() saveSuccess: boolean;
  @Input() contentID: string;
  @Input() courseID: string;
  @Input() blogID: string;
  @Input() chunk: any;
  @Input() context: 'create' | 'controller' = 'create';

  @Output() textGenerated = new EventEmitter<string>();
  @Output() createChunk = new EventEmitter<void>();
  @Output() uploadComplete = new EventEmitter<void>();
  // @Output() recordingSaved = new EventEmitter<{ file: Blob; type: 'video' | 'image' }>();
  @Output() submit = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();

  private overlay = inject(DynamicOverlayService);
  private contentStateService = inject(ContentStateService);
  private createChunkService = inject(CreateChunkService);
  private translate = inject(TranslateService);
  public data: CreateChunkOptionsOverlayData = inject(OVERLAY_DATA);
  private chunkService = inject(ChunkService);
  private storageService = inject(StorageService);
  private authService = inject(AuthService);
  canUseAI = this.authService.canUseAI();
  isScreenShareSupported = typeof navigator !== 'undefined' && !!navigator?.mediaDevices && !!navigator?.mediaDevices?.getDisplayMedia;
  constructor() {
    this.mode = this.data.mode;
    this.canUndo = this.data.canUndo;
    this.canRedo = this.data.canRedo;
    this.canCreate = this.data.canCreate;
    this.creatingChunk = this.data.creatingChunk;
    this.savingChunk = this.data.savingChunk;
    this.saveSuccess = this.data.saveSuccess;
    this.contentID = this.data.contentID;
    this.courseID = this.data.courseID;
    this.blogID = this.data.blogID;
    this.chunk = this.data.chunk;
    this.context = this.data.context || 'create';
  }

  onUploadComplete() {
    this.uploadComplete.emit();
    this.overlay.close();
  }

  onDeleted() {
    this.deleted.emit();
  }

  onAction() {
    this.overlay.close();
  }

  triggerAIPane(): void {
    const { chunkAboveId, chunkBelowId } = this.data;
    // Extract plain text or media url from chunk content
    let subject = '';
    let mediaContext: { url: string; type: 'audio' | 'file' | 'video' | 'image'; filename?: string } | undefined;

    if (this.chunk && this.chunk.editions && this.chunk.editions.length > 0) {
      // Find edition for current language
      const currentLang = this.translate.currentLang;
      let targetEdition = this.chunk.editions.find((e: any) => e.language === currentLang);

      // Fallback to first edition if current language not found
      if (!targetEdition) {
        targetEdition = this.chunk.editions[0];
      }

      if (targetEdition && targetEdition.content) {
        if (this.chunk.type === 'audio' || this.chunk.type === 'file' || this.chunk.type === 'video' || this.chunk.type === 'image') {
          mediaContext = {
            url: targetEdition.content,
            type: this.chunk.type,
            filename: targetEdition.filename,
          };
        } else {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = targetEdition.content;
          subject = tempDiv.textContent || tempDiv.innerText || '';
          subject = subject.trim();
        }
      }
    }

    this.contentStateService.openContentGenerator({
      initialSubject: this.chunk?.type === 'code' ? 'Code' : subject,
      mediaContext,
      context:
        this.chunk?.type === 'code'
          ? {
              id: this.chunk._id,
              title: this.chunk.title || this.translate.instant('CreateChunk.TextTypes.code'),
              name: this.chunk.title || this.translate.instant('CreateChunk.TextTypes.code'),
              type: 'chunk',
            }
          : undefined,
      insertAt: { above: chunkAboveId, below: chunkBelowId }, // Pass context if needed by generator later
    });
    // Close the overlay
    this.overlay.close();
  }

  async onMathEquationGenerated(latexCode: string): Promise<void> {
    const { chunkAboveId, chunkBelowId } = this.data;

    const chunkParams = {
      contentID: this.contentID,
      courseID: this.courseID,
      blogID: this.blogID,
      type: 'latex',
      content: latexCode,
      language: this.translate.currentLang,
    };

    const serverResponse = await firstValueFrom(
      this.chunkService.createChunk({
        chunks: [chunkParams],
        insertBelowID: chunkAboveId,
        insertAboveID: chunkBelowId,
      })
    );

    const finalChunks = serverResponse.map((res) => ({ ...res.chunk, status: 'saved' }));
    await this.storageService.bulkStore('chunks', finalChunks);

    await this.contentStateService.getPieces(true);

    this.onAction();
  }
}
